import {
  IMAGE_REPO_PREFIX,
  IMAGE_REPO_SUFFIX,
  NEXUS_REPO,
  RMO_ARTIFACTS_ENUM,
  TENANT_UI,
} from "../../../../utils/constants";

export const MODES = { STANDARD: "standard", UI: "ui", CUSTOM: "custom" };

// `defaultBaseConfigBranch` is a UI-only flag: it decides whether
// `baseConfigBranch` travels, and is never sent itself.
export const DEFAULT_BRANCH_FLAG = "defaultBaseConfigBranch";

export const BRANCH_HELPER =
  "Branch of the base config repo the Tf and DP libs are applied from. Leave on the release default unless you are testing a config change.";

export const SCOPE_FLAGS = [
  {
    key: "upgradeTfAndDpLibs",
    label: "Upgrade Tf and DP libs",
    description: "Re-applies Terraform and data-plane libraries.",
  },
  {
    key: "updateServices",
    label: "Update services",
    description: "Rolls every backend service to the target label.",
  },
  {
    key: "updateUi",
    label: "Update UI",
    description: "Replaces the web bundle only.",
  },
  {
    key: "updateSeedData",
    label: "Update seed data",
    description: "Re-seeds reference data; user data is kept.",
  },
];

export const DEFAULT_SCOPE = {
  upgradeTfAndDpLibs: true,
  updateServices: true,
  updateUi: true,
  updateSeedData: true,
  [DEFAULT_BRANCH_FLAG]: true,
};

export const UI_ONLY_SCOPE = {
  upgradeTfAndDpLibs: false,
  updateServices: false,
  updateUi: true,
  updateSeedData: false,
};

export const GROUP_LABELS = {
  dockerImages: "Docker images",
  helmCharts: "Helm charts",
};

// Copy hooks keyed by group so the UI never calls a chart a "service".
export const GROUP_NOUNS = {
  dockerImages: {
    singular: "image",
    plural: "images",
    nameColumn: "Image name",
  },
  helmCharts: { singular: "chart", plural: "charts", nameColumn: "Chart name" },
};

export const groupNoun = (group) =>
  GROUP_NOUNS[group] ?? {
    singular: "item",
    plural: "items",
    nameColumn: "Name",
  };

export const plural = (count, singular, pluralForm = `${singular}s`) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

export const enabledScopeCount = (scope) =>
  SCOPE_FLAGS.filter(({ key }) => scope?.[key]).length;

export const usesDefaultBranch = (scope) =>
  Boolean(scope?.[DEFAULT_BRANCH_FLAG]);

// Empty + "Custom" selected is the only blocking combination for the branch.
export const branchBlockedReason = (scope, baseConfigBranch) =>
  !usesDefaultBranch(scope) && !baseConfigBranch?.trim()
    ? "Enter a base config branch, or switch back to the release default"
    : "";

const apiScope = (scope) =>
  Object.fromEntries(
    Object.entries(scope ?? {}).filter(([key]) => key !== DEFAULT_BRANCH_FLAG)
  );

const branchPayload = (scope, baseConfigBranch) =>
  usesDefaultBranch(scope) ? {} : { baseConfigBranch: baseConfigBranch.trim() };

export const registryOptions = (group) =>
  IMAGE_REPO_SUFFIX.map(
    (suffix) => `${IMAGE_REPO_PREFIX[group] ?? ""}${suffix}`
  );

const defaultRegistry = (group) => registryOptions(group)[0];

// The manifest arrives as { group: { key: entry } } plus a scalar `release`.
// Artifacts stay gated — the bucket has no registry concept, so its rows cannot
// be edited with the same three controls.
export const buildGroups = (manifest) => {
  if (!manifest || typeof manifest !== "object") return [];
  return Object.keys(manifest)
    .filter(
      (group) =>
        group !== RMO_ARTIFACTS_ENUM &&
        typeof manifest[group] === "object" &&
        manifest[group] !== null
    )
    .map((group) => ({
      group,
      label: GROUP_LABELS[group] ?? group,
      rows: Object.keys(manifest[group])
        .sort()
        .map((key) => {
          const entry = manifest[group][key] ?? {};
          return {
            key,
            group,
            name: entry?.name ?? "",
            tag: entry?.tag ?? "",
            registry: defaultRegistry(group),
            repoName: entry?.repoName ?? "",
            commit: entry?.commit ?? "",
          };
        }),
    }));
};

export const cloneRows = (groups) =>
  groups.reduce((out, { group, rows }) => {
    out[group] = rows.reduce((byKey, row) => {
      byKey[row.key] = { name: row.name, tag: row.tag, registry: row.registry };
      return byKey;
    }, {});
    return out;
  }, {});

// Rows the user adds by hand for keys the manifest does not carry.
export const emptyExtra = (group) => ({
  key: "",
  name: "",
  tag: "",
  registry: defaultRegistry(group),
});

export const isCompleteExtra = (extra) =>
  Boolean(extra?.key?.trim() && extra?.tag?.trim());

// Returns an error string when the extra's key collides with an existing
// manifest row in the same group, or "" when it's safe to submit.
export const extraKeyError = (extra, rows) => {
  const trimmed = extra?.key?.trim();
  if (!trimmed) return "";
  return (rows ?? []).some((row) => row.key === trimmed)
    ? "Key already exists in the manifest — edit the row above instead"
    : "";
};

export const isOverridden = (baseRow, edited) =>
  Boolean(edited) &&
  (edited.name !== baseRow.name ||
    edited.tag !== baseRow.tag ||
    edited.registry !== baseRow.registry);

export const countOverrides = (groups, edits, extras) =>
  groups.reduce(
    (total, { group, rows }) =>
      total +
      rows.filter((row) => isOverridden(row, edits?.[group]?.[row.key]))
        .length +
      (extras?.[group] ?? []).filter(isCompleteExtra).length,
    0
  );

// One filter spans both groups: committed chips plus whatever is being typed.
// A row is in scope when any term appears in its key, name, or source repo;
// no terms at all means everything is in scope.
export const EMPTY_FILTER = { chips: [], text: "" };

const rowHasTerm = (row, term) => {
  const needle = term.trim().toLowerCase();
  return (
    Boolean(needle) &&
    [row.key, row.name, row.repoName].some((f) =>
      (f ?? "").toLowerCase().includes(needle)
    )
  );
};

export const filterTerms = (filter) =>
  [...(filter?.chips ?? []), filter?.text ?? ""].filter((t) => t.trim());

export const matchesFilter = (row, filter) => {
  const terms = filterTerms(filter);
  return !terms.length || terms.some((term) => rowHasTerm(row, term));
};

export const countInScope = (groups, filter) =>
  groups.reduce(
    (total, { rows }) =>
      total + rows.filter((row) => matchesFilter(row, filter)).length,
    0
  );

export const countRows = (groups) =>
  groups.reduce((total, { rows }) => total + rows.length, 0);

export const filterSuggestions = (groups, filter, limit = 8) => {
  const text = filter?.text ?? "";
  if (!text.trim()) return [];
  const taken = new Set((filter?.chips ?? []).map((c) => c.toLowerCase()));
  const keys = groups.flatMap(({ rows }) =>
    rows.filter((row) => rowHasTerm(row, text)).map((row) => row.key)
  );
  return [...new Set(keys)]
    .filter((key) => !taken.has(key.toLowerCase()))
    .slice(0, limit);
};

// Only changed fields travel; an untouched row contributes nothing. Added rows
// are keys absent from the manifest, so they always travel whole.
export const buildManifestOverride = (groups, edits, extras) =>
  groups.reduce((override, { group, rows }) => {
    const changed = rows.reduce((byKey, row) => {
      const edited = edits?.[group]?.[row.key];
      if (!isOverridden(row, edited)) return byKey;
      const patch = {};
      if (edited.name !== row.name) patch.name = edited.name;
      if (edited.tag !== row.tag) patch.tag = edited.tag;
      if (edited.registry !== row.registry) patch.repository = edited.registry;
      byKey[row.key] = patch;
      return byKey;
    }, {});

    const added = (extras?.[group] ?? [])
      .filter((extra) => isCompleteExtra(extra) && !extraKeyError(extra, rows))
      .reduce((byKey, extra) => {
        byKey[extra.key.trim()] = {
          name: extra.name,
          tag: extra.tag,
          repository: extra.registry,
        };
        return byKey;
      }, {});

    const merged = { ...changed, ...added };
    if (Object.keys(merged).length) override[group] = merged;
    return override;
  }, {});

export const buildPayload = ({
  mode,
  env,
  releaseLabel,
  scope,
  groups,
  edits,
  extras,
  uiTag,
  baseConfigBranch,
}) => {
  if (mode === MODES.UI) {
    const trimmedTag = uiTag.trim();
    return {
      url: env?.url,
      ...(trimmedTag
        ? {
            releaseManifestOverride: {
              [RMO_ARTIFACTS_ENUM]: {
                [TENANT_UI.rmoKey]: {
                  tag: trimmedTag,
                  repository: NEXUS_REPO.devPullRepo,
                },
              },
            },
          }
        : {}),
      ...UI_ONLY_SCOPE,
    };
  }

  if (mode === MODES.STANDARD) {
    return {
      url: env?.url,
      releaseLabel,
      ...apiScope(scope),
      ...branchPayload(scope, baseConfigBranch),
    };
  }

  return {
    url: env?.url,
    releaseLabel,
    releaseManifestOverride: buildManifestOverride(groups, edits, extras),
    ...apiScope(scope),
    ...branchPayload(scope, baseConfigBranch),
  };
};

export const scopeSummary = (scope) => {
  const on = SCOPE_FLAGS.filter(
    ({ key }) => key !== DEFAULT_BRANCH_FLAG && scope?.[key]
  ).map(({ label }) => label);
  return on.length ? on.join(" \u00b7 ") : "Nothing selected";
};
